<script setup lang="ts">
import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { Database, Plus, Trash2, RefreshCw, KeyRound } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

const LEFT_CARDINALITIES = [
  { value: '||', label: 'Exactamente Uno (||)' },
  { value: '|o', label: 'Cero o Uno (|o)' },
  { value: '}|', label: 'Uno o Muchos (}|)' },
  { value: '}o', label: 'Cero o Muchos (}o)' },
]

const RIGHT_CARDINALITIES = [
  { value: '||', label: 'Exactamente Uno (||)' },
  { value: 'o|', label: 'Cero o Uno (o|)' },
  { value: '|{', label: 'Uno o Muchos (|{)' },
  { value: 'o{', label: 'Cero o Muchos (o{)' },
]

const LINE_TYPES = [
  { value: '--', label: 'Línea Sólida (--)' },
  { value: '..', label: 'Línea Punteada (..)' },
]

const initNodes = () => {
  nodes.value = [
    { 
      id: 'e_CUSTOMER', type: 'entityNode', position: { x: 100, y: 100 }, 
      data: { name: 'CUSTOMER', attributes: [{ type: 'int', name: 'id', key: 'PK' }, { type: 'string', name: 'name', key: '' }] } 
    },
    { 
      id: 'e_ORDER', type: 'entityNode', position: { x: 450, y: 100 }, 
      data: { name: 'ORDER', attributes: [{ type: 'int', name: 'id', key: 'PK' }, { type: 'int', name: 'customerId', key: 'FK' }] } 
    }
  ]
  edges.value = [
    {
      id: 'e_CUSTOMER_ORDER', source: 'e_CUSTOMER', target: 'e_ORDER', type: 'custom',
      data: { leftCard: '||', lineType: '--', rightCard: 'o{', label: 'places' }
    }
  ]
}

watch(() => props.open, (val) => {
  if (!val) {
    nodes.value = []
    edges.value = []
    editableCode.value = ''
    return
  }

  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom',
    data: { 
      leftCard: '||',
      lineType: '--',
      rightCard: 'o{',
      label: 'relates'
    }
  })
}

const addEntityNode = () => {
  const id = `e_Entity_${Date.now()}`
  nodes.value.push({
    id,
    type: 'entityNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { name: `Entity_${Math.floor(Math.random()*1000)}`, attributes: [] }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\n'
  
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    positions[node.data.name] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += `erDiagram\n`
  
  // Relaciones
  edges.value.forEach(edge => {
    const sNode = nodes.value.find(n => n.id === edge.source)
    const tNode = nodes.value.find(n => n.id === edge.target)
    if (sNode && tNode) {
      const { leftCard, lineType, rightCard, label } = edge.data
      mermaid += `    ${sNode.data.name} ${leftCard}${lineType}${rightCard} ${tNode.data.name} : "${label}"\n`
    }
  })

  // Entidades
  nodes.value.forEach(node => {
    mermaid += `    ${node.data.name} {\n`
    node.data.attributes.forEach((attr: any) => {
      if (attr.name.trim()) {
        const type = attr.type.trim() || 'string'
        const key = attr.key ? ` ${attr.key}` : ''
        mermaid += `        ${type} ${attr.name.trim()}${key}\n`
      }
    })
    mermaid += `    }\n`
  })

  mermaid += '```\n'
  return mermaid
}

watch([nodes, edges], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const parsedNodes = new Map()
    const parsedEdges: any[] = []
    
    let currentEntity: string | null = null
    let savedPositions: Record<string, any> | null = null

    lines.forEach(line => {
      line = line.trim()
      
      // Parsear JSON oculto de posiciones
      if (line.startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }

      if (!line || line.startsWith('```') || line.startsWith('erDiagram') || line.startsWith('%%')) return

      // Entities with attributes block: ENTITY {
      if (line.endsWith('{')) {
        currentEntity = line.replace('{', '').trim()
        if (!parsedNodes.has(currentEntity)) {
          parsedNodes.set(currentEntity, { name: currentEntity, attributes: [] })
        }
        return
      }

      if (line === '}') {
        currentEntity = null
        return
      }

      if (currentEntity) {
        // Parse attributes inside block
        const parts = line.split(/\s+/)
        if (parts.length >= 2) {
          const type = parts[0]
          const name = parts[1]
          let key = parts.length > 2 ? parts[2] : ''
          // Ensure key is one of PK, FK, UK if valid
          if (!['PK', 'FK', 'UK'].includes(key.toUpperCase())) {
            key = ''
          }
          parsedNodes.get(currentEntity).attributes.push({ type, name, key })
        }
        return
      }

      // Parse relations: ENTITY1 ||--o{ ENTITY2 : "label"
      const relMatch = line.match(/^([a-zA-Z0-9_]+)\s+(\|\||\|o\}\||\}o|\|o|o\||o\{|\|\{)(--|\.\.)(\|\||\|o|o\||\}o|o\{|\|\{|\}\|)\s+([a-zA-Z0-9_]+)\s*:\s*"?(.*?)"?$/)
      
      // Fallback simple split if regex misses something due to complex combinations
      if (relMatch) {
        const [, sName, leftCard, lineType, rightCard, tName, label] = relMatch
        if (!parsedNodes.has(sName)) parsedNodes.set(sName, { name: sName, attributes: [] })
        if (!parsedNodes.has(tName)) parsedNodes.set(tName, { name: tName, attributes: [] })
        
        parsedEdges.push({
          sourceName: sName,
          targetName: tName,
          leftCard,
          lineType,
          rightCard,
          label
        })
      } else {
        // Intento manual más flexible
        const parts = line.split(':')
        if (parts.length === 2) {
          const leftSide = parts[0].trim()
          const label = parts[1].replace(/"/g, '').trim()
          
          const matchParts = leftSide.match(/^([a-zA-Z0-9_]+)\s+(.+?)\s+([a-zA-Z0-9_]+)$/)
          if (matchParts) {
            const sName = matchParts[1]
            const relationString = matchParts[2]
            const tName = matchParts[3]
            
            const lineType = relationString.includes('..') ? '..' : '--'
            const cards = relationString.split(lineType)
            if (cards.length === 2) {
              if (!parsedNodes.has(sName)) parsedNodes.set(sName, { name: sName, attributes: [] })
              if (!parsedNodes.has(tName)) parsedNodes.set(tName, { name: tName, attributes: [] })
              
              parsedEdges.push({
                sourceName: sName,
                targetName: tName,
                leftCard: cards[0],
                lineType,
                rightCard: cards[1],
                label
              })
            }
          }
        }
      }
    })

    const newNodes: any[] = []
    let x = 100, y = 100
    
    parsedNodes.forEach((data, name) => {
      const existing = nodes.value.find(n => n.data.name === name)
      
      let pos = { x, y }
      if (savedPositions && savedPositions[name]) {
        pos = savedPositions[name]
      } else if (existing) {
        pos = existing.position
      } else {
        y += 50
      }

      newNodes.push({
        id: existing?.id || `e_${name}_${Date.now()}_${Math.random()}`,
        type: 'entityNode',
        position: pos,
        data: { ...data }
      })
      
      if (!savedPositions || !savedPositions[name]) {
        x += 20
      }
    })

    const newEdges: any[] = []
    parsedEdges.forEach(pe => {
      const sNode = newNodes.find(n => n.data.name === pe.sourceName)
      const tNode = newNodes.find(n => n.data.name === pe.targetName)
      if (sNode && tNode) {
        newEdges.push({
          id: `e-${sNode.id}-${tNode.id}-${Date.now()}-${Math.random()}`,
          source: sNode.id,
          target: tNode.id,
          type: 'custom',
          data: {
            leftCard: pe.leftCard,
            lineType: pe.lineType,
            rightCard: pe.rightCard,
            label: pe.label
          }
        })
      }
    })

    nodes.value = newNodes
    edges.value = newEdges

  } catch (error) {
    console.error("Error parsing ER code:", error)
  } finally {
    setTimeout(() => { isParsing.value = false }, 100)
  }
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (e) {
    console.error('Failed to copy', e)
  }
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama Entidad-Relación (ER)"
    description="Modela bases de datos. Crea entidades, añade atributos con sus tipos y llaves (PK/FK), y conéctalas con sus cardinalidades correctas."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <Database class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addEntityNode"><Plus class="w-4 h-4 mr-2" /> Añadir Entidad</Button>
    </template>

    <template #canvas>
      <VueFlow 
        v-model:nodes="nodes" 
        v-model:edges="edges"
        @connect="handleConnect"
        :default-zoom="1"
      >
        <Background />
        <Controls />

        <!-- Node: Entity -->
        <template #node-entityNode="nodeProps">
          <div class="relative group border-2 border-primary rounded-md bg-card shadow-md flex flex-col min-w-[220px] font-mono text-sm overflow-hidden">
            <!-- Header (Entity Name) -->
            <div class="bg-primary p-2 font-bold text-center text-primary-foreground border-b-2 border-primary">
              <input v-model="nodeProps.data.name" class="bg-transparent text-center w-full focus:outline-none" placeholder="ENTITY_NAME" />
            </div>
            
            <!-- Attributes -->
            <div class="p-2 min-h-[40px] flex flex-col gap-1 bg-card">
              <div v-for="(attr, i) in nodeProps.data.attributes" :key="i" class="flex items-center gap-1 group/item">
                <input v-model="nodeProps.data.attributes[i].type" class="bg-transparent text-xs w-[60px] text-muted-foreground focus:outline-none focus:bg-muted/50 rounded px-1" placeholder="int" />
                <input v-model="nodeProps.data.attributes[i].name" class="bg-transparent text-xs flex-1 font-semibold focus:outline-none focus:bg-muted/50 rounded px-1" placeholder="id" />
                
                <!-- Key Selector -->
                <select v-model="nodeProps.data.attributes[i].key" class="text-[10px] bg-transparent outline-none p-0 appearance-none font-bold text-yellow-600 dark:text-yellow-400 cursor-pointer text-center w-6">
                  <option value="">-</option>
                  <option value="PK">PK</option>
                  <option value="FK">FK</option>
                  <option value="UK">UK</option>
                </select>
                
                <button @click="nodeProps.data.attributes.splice(i, 1)" class="text-destructive opacity-0 group-hover/item:opacity-100 shrink-0"><Trash2 class="w-3 h-3" /></button>
              </div>
              <button @click="nodeProps.data.attributes.push({ type: 'string', name: 'newField', key: '' })" class="text-[10px] text-muted-foreground hover:text-primary text-left mt-2 flex items-center gap-1">
                <Plus class="w-3 h-3" /> Añadir atributo
              </button>
            </div>

            <!-- Handles (4 lados) -->
            <Handle type="target" :position="Position.Top" id="top" />
            <Handle type="source" :position="Position.Bottom" id="bottom" />
            <Handle type="source" :position="Position.Right" id="right" />
            <Handle type="target" :position="Position.Left" id="left" />

            <!-- Delete Button -->
            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar Entidad">×</button>
            </div>
          </div>
        </template>

        <!-- Edge: ER Relation -->
        <template #edge-custom="edgeProps">
          <BaseEdge 
            :id="edgeProps.id" 
            :path="getBezierPath(edgeProps)[0]" 
            class="vue-flow__edge-path" 
            :style="{ 
              strokeWidth: 2, 
              stroke: 'hsl(var(--primary))', 
              strokeDasharray: edgeProps.data.lineType === '..' ? '5,5' : '0' 
            }"
          />
          <EdgeLabelRenderer>
            <div
              :style="{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${getBezierPath(edgeProps)[1]}px,${getBezierPath(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }"
              class="nodrag nopan bg-card border shadow-md rounded p-1 group z-10 flex flex-col gap-1 w-[200px]"
            >
              <!-- Selector de Cardinalidad -->
              <div class="flex items-center gap-1 w-full justify-between">
                <select v-model="edgeProps.data.leftCard" class="text-[10px] border rounded bg-background focus:outline-none w-[60px]" title="Cardinalidad Izquierda (Origen)">
                  <option v-for="c in LEFT_CARDINALITIES" :key="c.value" :value="c.value">{{ c.value }}</option>
                </select>
                
                <select v-model="edgeProps.data.lineType" class="text-[10px] border rounded bg-background focus:outline-none flex-1 text-center font-bold text-muted-foreground" title="Tipo de Línea">
                  <option v-for="l in LINE_TYPES" :key="l.value" :value="l.value">{{ l.value }}</option>
                </select>
                
                <select v-model="edgeProps.data.rightCard" class="text-[10px] border rounded bg-background focus:outline-none w-[60px]" title="Cardinalidad Derecha (Destino)">
                  <option v-for="c in RIGHT_CARDINALITIES" :key="c.value" :value="c.value">{{ c.value }}</option>
                </select>
              </div>
              
              <!-- Etiqueta -->
              <input 
                v-model="edgeProps.data.label" 
                class="w-full bg-background text-center border rounded p-1 text-[10px] text-foreground focus:outline-none font-mono"
                placeholder="Verbo de relación..."
              />

              <!-- Delete Edge -->
              <button 
                @click="deleteEdge(edgeProps.id)" 
                class="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Eliminar Conexión"
              >×</button>
            </div>
          </EdgeLabelRenderer>
        </template>
      </VueFlow>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
